import { apiClient } from "@/lib/api/client";

export type RolePermissionDto = {
  module_key: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_export: boolean;
  can_manage_permissions: boolean;
  updated_at?: string | null;
};

export type RoleDto = {
  id: string;
  tenant_id: string | null;
  key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_default: boolean;
  is_active: boolean;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  permissions: RolePermissionDto[];
};

export type RolesListResponse = {
  items: RoleDto[];
  total: number;
};

export type RoleDevicePermissionsResponse = {
  role_id: string;
  device_id: string;
  permissions: RolePermissionDto[];
};

export type RoleUpsertBody = {
  name: string;
  description?: string | null;
  is_active?: boolean;
  permissions?: RolePermissionDto[];
};

export async function listRoles(tenantId?: string | null, includeDefault = true): Promise<RolesListResponse> {
  const params = {
    ...(tenantId ? { tenant_id: tenantId } : {}),
    include_default: includeDefault,
  };
  const { data } = await apiClient.get<RolesListResponse>("/roles", { params });
  return data;
}

export async function createRole(body: RoleUpsertBody): Promise<RoleDto> {
  const { data } = await apiClient.post<RoleDto>("/roles", body);
  return data;
}

export async function updateRole(roleId: string, body: Partial<RoleUpsertBody>): Promise<RoleDto> {
  const { data } = await apiClient.put<RoleDto>(`/roles/${roleId}`, body);
  return data;
}

export async function updateRolePermissions(roleId: string, permissions: RolePermissionDto[]): Promise<RoleDto> {
  const { data } = await apiClient.put<RoleDto>(`/roles/${roleId}/permissions`, { permissions });
  return data;
}

export async function updateDefaultRoleWorkspaceDevicePermissions(
  roleId: string,
  permissions: RolePermissionDto[],
): Promise<RoleDto> {
  const { data } = await apiClient.put<RoleDto>(`/roles/${roleId}/workspace-device-permissions`, { permissions });
  return data;
}

export async function getRoleDeviceModulePermissions(
  roleId: string,
  deviceId: string,
): Promise<RoleDevicePermissionsResponse> {
  const { data } = await apiClient.get<RoleDevicePermissionsResponse>(`/roles/${roleId}/device-module-permissions`, {
    params: { device_id: deviceId },
  });
  return data;
}

export async function updateRoleDeviceModulePermissions(
  roleId: string,
  deviceId: string,
  permissions: RolePermissionDto[],
): Promise<RoleDevicePermissionsResponse> {
  const { data } = await apiClient.put<RoleDevicePermissionsResponse>(
    `/roles/${roleId}/device-module-permissions`,
    { permissions },
    { params: { device_id: deviceId } },
  );
  return data;
}

export async function deleteRole(roleId: string): Promise<void> {
  await apiClient.delete(`/roles/${roleId}`);
}
